import * as PortOne from "@portone/browser-sdk/v2";
import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { toast } from "react-toastify";
import styled, { css } from "styled-components";

import { authAtom } from "@core/atoms/auth.atom";
import useCancelSubscription from "@core/hooks/mutations/subscription/useCancelSubscription";
import useCreateSubscription from "@core/hooks/mutations/subscription/useCreateSubscription";
import useSubscription from "@core/hooks/queries/subscription/useSubscription";
import useIsGuest from "@core/hooks/useIsGuest";
import useModalState from "@core/hooks/useModalState";
import queryKeyGenerator from "@core/utils/queryKeyGenerator";

import Button from "@components/Button";
import Modal from "@components/Modal";

import DefaultLayout from "@layouts/DefaultLayout";

// 포트원 V2 설정
const STORE_ID = process.env.REACT_APP_PORTONE_STORE_ID ?? "";
const PAYMENT_METHODS = [
  {
    id: "kakaopay",
    label: "카카오페이",
    channelKey: process.env.REACT_APP_PORTONE_KAKAOPAY_CHANNEL_KEY ?? "",
    billingKeyMethod: "EASY_PAY" as const,
    icon: "💛",
  },
  {
    id: "tosspayments",
    label: "토스페이먼츠",
    channelKey: process.env.REACT_APP_PORTONE_TOSSPAYMENTS_CHANNEL_KEY ?? "",
    billingKeyMethod: "CARD" as const,
    icon: "💙",
  },
];

const SubscriptionIndex = () => {
  const auth = useAtomValue(authAtom);
  const isGuest = useIsGuest();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(PAYMENT_METHODS[0].id);
  const [cancelModal, toggleCancelModal] = useModalState<boolean>();

  const subscription = useSubscription({
    enabled: !isGuest,
  });

  const createSubscription = useCreateSubscription({
    onSuccess: () => {
      toast.success("구독이 시작되었습니다!");
      queryClient.invalidateQueries({
        queryKey: queryKeyGenerator.getSubscription(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeyGenerator.getMyInformation(),
      });
    },
    onError: () => {
      toast.error("구독 생성에 실패했습니다. 다시 시도해주세요.");
    },
  });

  const cancelSubscriptionMutation = useCancelSubscription({
    onSuccess: () => {
      toast.success("구독이 해지되었습니다. 남은 기간은 계속 이용 가능합니다.");
      toggleCancelModal();
      queryClient.invalidateQueries({
        queryKey: queryKeyGenerator.getSubscription(),
      });
    },
  });

  const handleSubscribe = async () => {
    if (isGuest) {
      toast.error("로그인 후 이용해주세요.");
      return;
    }

    const method =
      PAYMENT_METHODS.find((m) => m.id === selectedMethod) ||
      PAYMENT_METHODS[0];

    if (!STORE_ID || !method.channelKey) {
      toast.error("결제 설정이 완료되지 않았습니다.");
      return;
    }

    setIsProcessing(true);

    try {
      const response = await PortOne.requestIssueBillingKey({
        storeId: STORE_ID,
        channelKey: method.channelKey,
        billingKeyMethod: method.billingKeyMethod,
        issueId: `issue-${Date.now()}`,
        issueName: "로아투두 월 구독",
      });

      if (!response || response.code !== undefined) {
        toast.error(response?.message || "빌링키 발급에 실패했습니다.");
        return;
      }

      createSubscription.mutate({ customerUid: response.billingKey! });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Subscription error:", error);
      toast.error("결제 처리 중 오류가 발생했습니다.");
    } finally {
      setIsProcessing(false);
    }
  };

  const isActive = subscription.data?.status === "ACTIVE";
  const isCancelled = subscription.data?.status === "CANCELLED";
  const isPaymentFailed = subscription.data?.status === "PAYMENT_FAILED";

  const hasExistingAds =
    auth.adsDate != null &&
    new Date(auth.adsDate) > new Date() &&
    !subscription.data;
  const existingRemainingDays = auth.adsDate
    ? Math.ceil(
        (new Date(auth.adsDate).getTime() - new Date().getTime()) /
          (1000 * 3600 * 24)
      )
    : 0;

  return (
    <DefaultLayout pageTitle="구독">
      <Container>
        <HeroSection>
          <HeroTitle>로아투두 구독</HeroTitle>
          <HeroSubtitle>
            월 500원으로 광고 없는 깔끔한 환경을 즐기세요
          </HeroSubtitle>
        </HeroSection>

        <BenefitSection>
          <SectionTitle>구독 혜택</SectionTitle>
          <BenefitGrid>
            <BenefitCard>
              <BenefitIcon>&#x1f6ab;</BenefitIcon>
              <BenefitTitle>광고 제거</BenefitTitle>
              <BenefitDesc>
                모든 페이지에서 광고 없이
                <br />
                깔끔하게 이용할 수 있어요
              </BenefitDesc>
            </BenefitCard>
            <BenefitCard>
              <BenefitIcon>&#x1f4b3;</BenefitIcon>
              <BenefitTitle>자동 결제</BenefitTitle>
              <BenefitDesc>
                매월 자동 결제로
                <br />
                번거로움 없이 이용
              </BenefitDesc>
            </BenefitCard>
            <BenefitCard>
              <BenefitIcon>&#x2b50;</BenefitIcon>
              <BenefitTitle>프리미엄 기능</BenefitTitle>
              <BenefitDesc>
                추가 프리미엄 기능
                <br />
                제공 예정
              </BenefitDesc>
            </BenefitCard>
          </BenefitGrid>
        </BenefitSection>

        <PriceSection>
          <PriceCard>
            <PriceLabel>월 구독</PriceLabel>
            <PriceAmount>
              <span>500</span>원/월
            </PriceAmount>
            <ServicePeriod>서비스 제공 기간: 결제 완료 즉시 적용</ServicePeriod>

            {isActive && (
              <StatusBadge $status="active">구독 중</StatusBadge>
            )}
            {isCancelled && (
              <StatusBadge $status="cancelled">해지 예정</StatusBadge>
            )}
            {isPaymentFailed && (
              <StatusBadge $status="failed">결제 실패</StatusBadge>
            )}

            {subscription.data && (
              <SubscriptionInfo>
                {isActive && (
                  <InfoRow>
                    <span>다음 결제일</span>
                    <strong>{subscription.data.nextPaymentDate}</strong>
                  </InfoRow>
                )}
                {isCancelled && (
                  <InfoRow>
                    <span>이용 만료일</span>
                    <strong>{subscription.data.nextPaymentDate}</strong>
                  </InfoRow>
                )}
                <InfoRow>
                  <span>구독 시작일</span>
                  <strong>
                    {subscription.data.createdDate?.split("T")[0]}
                  </strong>
                </InfoRow>
              </SubscriptionInfo>
            )}

            {!subscription.data || subscription.data.status === "EXPIRED" || isPaymentFailed ? (
              <>
                {hasExistingAds && (
                  <ExistingAdsNotice>
                    <p>
                      기존 후원으로 광고 제거가{" "}
                      <strong>{existingRemainingDays}일</strong> 남아있습니다.
                    </p>
                    <p>구독 시 남은 기간에 30일이 추가됩니다.</p>
                  </ExistingAdsNotice>
                )}
                <PaymentMethodList>
                  {PAYMENT_METHODS.map((m) => (
                    <PaymentMethodItem
                      key={m.id}
                      $isSelected={selectedMethod === m.id}
                      onClick={() => setSelectedMethod(m.id)}
                    >
                      <span>{m.icon}</span>
                      <span>{m.label}</span>
                    </PaymentMethodItem>
                  ))}
                </PaymentMethodList>
                <Button
                  css={subscribeButtonCss}
                  onClick={handleSubscribe}
                  disabled={isProcessing || createSubscription.isPending}
                  fullWidth
                >
                  {isProcessing ? "결제 진행 중..." : "구독하기"}
                </Button>
              </>
            ) : isActive ? (
              <Button
                css={cancelButtonCss}
                onClick={() => toggleCancelModal(true)}
                variant="outlined"
                fullWidth
              >
                구독 해지
              </Button>
            ) : null}
          </PriceCard>
        </PriceSection>

        <NoticeSection>
          <SectionTitle>안내사항</SectionTitle>
          <NoticeList>
            <li>구독 결제 완료 후 광고 제거 혜택이 즉시 적용됩니다.</li>
            <li>구독은 매월 자동으로 갱신됩니다.</li>
            <li>해지 시 남은 기간까지는 혜택이 유지됩니다.</li>
            <li>결제 실패 시 3일간 재시도 후 구독이 만료됩니다.</li>
            <li>기존 후원으로 적용된 광고 제거 기간은 그대로 유지됩니다.</li>
          </NoticeList>
        </NoticeSection>
      </Container>

      <Modal
        title="구독 해지"
        isOpen={!!cancelModal}
        onClose={toggleCancelModal}
        buttons={[
          {
            label: "해지하기",
            onClick: () => cancelSubscriptionMutation.mutate(),
          },
          {
            label: "취소",
            onClick: () => toggleCancelModal(),
          },
        ]}
      >
        <CancelModalContent>
          정말로 구독을 <strong>해지</strong>하시겠어요?
          <br />
          남은 기간까지는 광고 제거 혜택이 유지됩니다.
        </CancelModalContent>
      </Modal>
    </DefaultLayout>
  );
};

export default SubscriptionIndex;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const HeroSection = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const HeroTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.app.text.main};
  margin-bottom: 12px;
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.app.text.dark2};
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.app.text.main};
  margin-bottom: 16px;
`;

const BenefitSection = styled.div``;

const BenefitGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  ${({ theme }) => theme.medias.max768} {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 16px;
  background: ${({ theme }) => theme.app.bg.gray2};
  text-align: center;
`;

const BenefitIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const BenefitTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.app.text.main};
  margin-bottom: 8px;
`;

const BenefitDesc = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.app.text.dark2};
  line-height: 1.6;
`;

const PriceSection = styled.div`
  display: flex;
  justify-content: center;
`;

const PriceCard = styled.div`
  padding: 32px;
  border: 2px solid ${({ theme }) => theme.app.palette.blue[350]};
  border-radius: 16px;
  background: ${({ theme }) => theme.app.bg.gray2};
  text-align: center;
  width: 100%;
  max-width: 400px;
`;

const PriceLabel = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.app.text.dark2};
  margin-bottom: 8px;
`;

const ServicePeriod = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.app.palette.blue[350]};
  font-weight: 600;
  margin-bottom: 16px;
`;

const PriceAmount = styled.div`
  font-size: 18px;
  color: ${({ theme }) => theme.app.text.main};
  margin-bottom: 8px;

  span {
    font-size: 36px;
    font-weight: 700;
  }
`;

const StatusBadge = styled.div<{
  $status: "active" | "cancelled" | "failed";
}>`
  display: inline-block;
  padding: 4px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 16px;

  ${({ $status }) =>
    $status === "active" &&
    css`
      background: #e8f5e9;
      color: #2e7d32;
    `}

  ${({ $status }) =>
    $status === "cancelled" &&
    css`
      background: #fff3e0;
      color: #e65100;
    `}

  ${({ $status }) =>
    $status === "failed" &&
    css`
      background: #ffebee;
      color: #c62828;
    `}
`;

const SubscriptionInfo = styled.div`
  margin-bottom: 20px;
  padding: 16px;
  background: ${({ theme }) => theme.app.bg.main};
  border-radius: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 14px;
  color: ${({ theme }) => theme.app.text.dark2};

  strong {
    color: ${({ theme }) => theme.app.text.main};
  }
`;

const ExistingAdsNotice = styled.div`
  padding: 16px;
  margin-bottom: 12px;
  background: ${({ theme }) => theme.app.bg.main};
  border-radius: 12px;
  text-align: center;
  color: ${({ theme }) => theme.app.text.dark2};
  font-size: 14px;
  line-height: 1.8;

  strong {
    color: ${({ theme }) => theme.app.palette.blue[350]};
  }
`;

const PaymentMethodList = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const PaymentMethodItem = styled.button<{ $isSelected: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 12px 8px;
  border-radius: 12px;
  border: 2px solid
    ${({ $isSelected, theme }) =>
      $isSelected ? theme.app.palette.blue[350] : theme.app.border};
  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.app.bg.main : "transparent"};
  color: ${({ theme }) => theme.app.text.main};
  font-size: 13px;
  font-weight: ${({ $isSelected }) => ($isSelected ? 600 : 400)};
  cursor: pointer;
  transition: all 0.15s;
`;

const subscribeButtonCss = css`
  background: ${({ theme }) => theme.app.palette.blue[350]};
  color: white;
  font-weight: 700;
  font-size: 16px;
  padding: 12px;
`;

const cancelButtonCss = css`
  font-size: 14px;
`;

const NoticeSection = styled.div`
  padding: 24px;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 16px;
  background: ${({ theme }) => theme.app.bg.gray2};
`;

const NoticeList = styled.ul`
  padding-left: 20px;
  list-style-type: disc;
  color: ${({ theme }) => theme.app.text.dark2};

  li {
    margin-bottom: 8px;
    line-height: 1.6;
    font-size: 14px;
  }
`;

const CancelModalContent = styled.div`
  color: ${({ theme }) => theme.app.text.black};

  strong {
    color: #ff5a5a;
  }
`;
